<!-- =========================================================================================
    File Name: SOPPicker.vue
    Description: Reusable SOP picker component
    ----------------------------------------------------------------------------------------
    Item Name: TCVelik - Onboarding Platform
    Author: TCVelik Team
========================================================================================== -->

<template>
  <div class="sop-picker">
    <!-- SOP Selection Display -->
    <div class="sop-selection">
      <div v-if="!selectedSOP" class="no-sop-selected">
        <span class="no-sop-text">No SOP selected</span>
        <a href="#" @click.prevent="openSOPSelector" class="change-link">Select</a>
      </div>
      <div v-else class="sop-selected">
        <div class="sop-info">
          <h4 class="sop-title">{{ selectedSOP.name }}</h4>
          <p class="sop-description">{{ selectedSOP.description || 'No description available' }}</p>
        </div>
        <a href="#" @click.prevent="openSOPSelector" class="change-link">Change</a>
      </div>
    </div>

    <!-- SOP Selector Modal -->
    <vs-popup title="Select SOP" :active.sync="showSOPSelector" fullscreen>
      <div class="sop-selector-content">
        <div v-if="availableSOPs.length > 0" class="sop-list-container">
          <div class="sop-list-header mb-4">
            <h3 class="text-lg font-semibold">Available SOPs</h3>
            <p class="text-gray-600">Select an SOP</p>
          </div>

          <div class="sop-table-container">
            <vs-table class="sop-table" :data="availableSOPs" pagination max-items="10" search>
              <template slot="thead">
                <vs-th sort-key="id">ID</vs-th>
                <vs-th sort-key="name">Title</vs-th>
                <vs-th sort-key="description">Description</vs-th>
                <vs-th>Actions</vs-th>
              </template>

              <template slot-scope="{data}">
                <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                  <vs-td :data="tr.id">
                    <div class="sop-id">{{ tr.id }}</div>
                  </vs-td>
                  
                  <vs-td :data="tr.name">
                    <div class="sop-name">
                      <strong>{{ tr.name }}</strong>
                    </div>
                  </vs-td>
                  
                  <vs-td :data="tr.description">
                    <div class="sop-description">
                      {{ tr.description || 'No description available' }}
                    </div>
                  </vs-td>
                  
                  <vs-td>
                    <vs-button 
                      @click="selectSOP(tr)"
                      color="primary" 
                      size="small">
                      Select
                    </vs-button>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </div>
        </div>
        
        <div v-else class="empty-sops">
          <div class="text-center py-12 text-gray-500">
            <i class="fas fa-file-alt text-4xl mb-4"></i>
            <h3 class="text-xl font-semibold mb-2">No SOPs available</h3>
            <p class="text-gray-600">There are no SOPs available for selection.</p>
          </div>
        </div>

        <div class="modal-actions">
          <vs-button @click="closeSOPSelector" color="warning" type="border">
            Cancel
          </vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import TCVelikClient from '@/TCVelikClient/TCVelikClient.js';

export default {
  name: 'SOPPicker',
  props: {
    value: {
      type: Object,
      default: null
    },
    label: {
      type: String,
      default: 'Select SOP'
    }
  },
  data() {
    return {
      showSOPSelector: false,
      availableSOPs: [],
      selectedSOP: null
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.selectedSOP = newVal;
      }
    },
    selectedSOP(newVal) {
      this.$emit('input', newVal);
    }
  },
  async mounted() {
    await this.loadAvailableSOPs();
  },
  methods: {
    async loadAvailableSOPs() {
      try {
        const query = `
          query GetOnboardings {
            onboardings {
              id
              name
              description
              created
              lastModified
            }
          }
        `;

        const response = await TCVelikClient.PostRelative('/api/graphql', {
          query: query
        });

        if (response && response.data && response.data.data && response.data.data.onboardings) {
          this.availableSOPs = response.data.data.onboardings;
        } else {
          this.availableSOPs = [];
        }
      } catch (error) {
        console.error('Error loading available SOPs:', error);
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load available SOPs',
          color: 'danger'
        });
        this.availableSOPs = [];
      }
    },

    openSOPSelector() {
      this.showSOPSelector = true;
    },

    closeSOPSelector() {
      this.showSOPSelector = false;
    },

    selectSOP(sop) {
      this.selectedSOP = sop;
      this.closeSOPSelector();
    }
  }
}
</script>

<style lang="scss" scoped>
/* SOP Selection Styles */
.sop-selection {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
  background-color: #f9fafb;
}

.no-sop-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.no-sop-text {
  color: #6b7280;
  font-style: italic;
}

.sop-selected {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.sop-info {
  flex: 1;
}

.sop-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
}

.sop-description {
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.4;
}

.change-link {
  color: #3b82f6;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
}

.change-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

/* SOP Selector Modal Styles */
.sop-selector-content {
  padding: 1rem;
  height: 100%;
  overflow-y: auto;
}

.sop-list-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.sop-list-header {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem;
}

.sop-table-container {
  flex: 1;
  overflow: auto;
}

.sop-table {
  margin: 0;
}

.sop-table .vs-table--tbody .vs-table--tr {
  border-bottom: 1px solid #f3f4f6;
}

.sop-table .vs-table--tbody .vs-table--tr:hover {
  background-color: #f9fafb;
}

.sop-table .vs-table--thead .vs-table--th {
  background-color: #f8fafc;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
}

.sop-id {
  font-weight: 500;
  color: #6b7280;
}

.sop-name strong {
  color: #1f2937;
}

.sop-description {
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.4;
}

.empty-sops {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}
</style>
